// How a tech is named on screen: first name and last initial ("Rafael M."). Jobs, labor and
// notes still save techs by first name ("Rafael", "Rafael, Dante"), and every match between a
// job and a person goes by that first name, so this only changes what people read.
// The labels come from /users (displayName). Pages that have the staff list hand it over
// (rememberTechs); the copy in localStorage lets every page, and the office shell's iframes,
// show labels at once without loading /users themselves.

const KEY = 'techLabels';
let labels = {};
try { labels = JSON.parse(localStorage.getItem(KEY)) || {}; } catch { labels = {}; }

const firstKey = name => String(name || '').trim().split(/\s+/)[0].toLowerCase();

/** "Rafael Martinez" → "Rafael M."; one-word names stay as they are. */
export function shortName(displayName) {
  const parts = String(displayName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || '';
  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
}

const isTech = u => String(u.role || '').split(',').some(r => r.trim().toLowerCase() === 'tech');

/**
 * Learns the labels from a staff list: an array of user records or the /users object. When two
 * people share a first name, a tech wins over other staff; two techs (Settings blocks that) or
 * two other staff are left as the plain first name, since a saved first name can't say which.
 */
export function rememberTechs(users) {
  const list = (Array.isArray(users) ? users : Object.values(users || {}))
    .filter(u => u && typeof u === 'object' && u.displayName);
  const pick = {};
  list.forEach(u => {
    const k = firstKey(u.displayName);
    const cur = pick[k];
    if (!cur) pick[k] = { u, n: 1 };
    else if (isTech(u) && !isTech(cur.u)) pick[k] = { u, n: 1 };
    else if (isTech(u) === isTech(cur.u)) cur.n += 1;
  });
  const next = {};
  Object.entries(pick).forEach(([k, { u, n }]) => { if (n === 1) next[k] = shortName(u.displayName); });
  labels = next;
  try { localStorage.setItem(KEY, JSON.stringify(labels)); } catch { /* storage blocked */ }
}

/** One tech's name as saved (first name, or a full name) → "Rafael M."; unknown names as they are. */
export function techLabel(name) {
  const raw = String(name ?? '').trim();
  if (!raw) return '';
  return labels[firstKey(raw)] || raw;
}

/** A saved Techs list ("Rafael, Dante") → "Rafael M., Dante K.". */
export function techsLabel(value) {
  return String(value ?? '').split(',').map(s => s.trim()).filter(Boolean).map(techLabel).join(', ');
}
